// @ts-check

export const columnSettings = {
  companyName: {
    name: 'name',
    sort: 'asc',
    defaultSort: 'asc',
    displayName: 'BUYER_COMPANY_NAME',
    class: 'column-sortable',
    rowAlign: 'left'
  },
  subid: {
    name: 'subId',
    sort: 'asc',
    displayName: 'BUYER_SUBID',
    class: 'column-sortable',
    rowAlign: 'left'
  },
  buyerCanPauseTargets: {
    name: 'canPauseTargets',
    displayName: 'BUYER_CAN_PAUSE_TARGETS_HINT',
    sort: 'asc',
    class: 'tablesort-sortable',
    rowAlign: 'center'
  },
  buyerCanSetTargetConcurrency: {
    name: 'canSetConcurrencyCaps',
    sort: 'asc',
    displayName: 'BUYER_CAN_SET_TARGET_CONCURRENCY_HINT',
    class: 'tablesort-sortable text-center',
    rowAlign: 'center'
  },
  buyerCanDisputeConversion: {
    name: 'canDisputeConversions',
    sort: 'asc',
    displayName: 'BUYER_CAN_DISPUTE_CONVERSION_HINT',
    class: 'tablesort-sortable text-center',
    rowAlign: 'center'
  },
  currentHourSum: {
    name: 'usageStats.currentHourSum',
    sort: 'asc',
    displayName: 'STATS_HOUR',
    class: 'tablesort-sortable text-center p-l-15',
    rowAlign: 'center'
  },
  currentDaySum: {
    name: 'usageStats.currentDaySum',
    sort: 'asc',
    displayName: 'STATS_DAY',
    class: 'tablesort-sortable text-center p-l-15',
    rowAlign: 'center'
  },
  currentMonthSum: {
    name: 'usageStats.currentMonthSum',
    sort: 'asc',
    displayName: 'STATS_MONTH',
    class: 'tablesort-sortable text-center p-l-15',
    rowAlign: 'center'
  },
  totalSum: {
    name: 'usageStats.totalSum',
    sort: 'asc',
    displayName: 'STATS_TOTAL',
    class: 'tablesort-sortable text-center p-l-15',
    rowAlign: 'center'
  },
  status: {
    name: 'tStatus.uiStatus.status',
    displayName: 'STATUS_TEXT',
    sort: 'asc',
    class: 'tablesort-sortable',
    rowAlign: 'center'
  },
  restrictDuplicate: {
    name: 'restrictDuplicate',
    displayName: 'BUYER_DUPLICATE_RESTRICTION',
    rowAlign: 'left'
  }
};

export const defaultColumns = [
  'companyName',
  'subid',
  'buyerCanPauseTargets',
  'buyerCanSetTargetConcurrency',
  'buyerCanDisputeConversion',
  'restrictDuplicate',
  'currentHourSum',
  'currentDaySum',
  'currentMonthSum',
  'totalSum',
  'status'
];

export const blockReasonTranslation = {
  OPEN: {
    className: 'green_status',
    tooltip: 'BUYERS_STATUS_OPEN',
    status: 'open'
  },
  CLOSED: {
    className: 'red_status',
    tooltip: 'BUYERS_STATUS_DISABLED',
    status: 'red-disabled'
  },
  BuyerConcurrencyHit: {
    className: 'yellow_status',
    tooltip: 'BUYERS_STATUS_CC_HIT',
    status: 'capped'
  },
  BuyerIsCapped: {
    className: 'yellow_status',
    tooltip: 'BUYERS_STATUS_CAPPED',
    status: 'capped'
  },
  IsClosed: {
    className: 'red_status',
    tooltip: 'BUYERS_STATUS_CLOSED',
    status: 'red-disabled'
  }
};

export const duplicateRestrictionTypes = [
  {
    name: 'Do Not Restrict',
    value: 'none',
    default: true
  },
  {
    name: 'By Campaign - All targets',
    value: 'buyer'
  },
  {
    name: 'By Campaign - Individual Target',
    value: 'target'
  },
  {
    name: 'Setup not supported',
    value: 'not supported'
  }
];

export default class BuyerServiceV2 {
  /**
   * @ngInject
   * @param {import('angular').IHttpService} $http
   * @param {*} ngAuthSettings
   * @param {*} promiseCacheService
   * @param {*} statsService
   * @param {import('lodash').LoDashStatic} _
   */
  constructor($http, ngAuthSettings, promiseCacheService, statsService, _) {
    this.$http = $http;
    this.ngAuthSettings = ngAuthSettings;
    this.serviceBaseUri = ngAuthSettings.apiServiceBaseUri;
    this.promiseCacheService = promiseCacheService;
    this.statsService = statsService;
    this._ = _;
    this.columnSettings = _.cloneDeep(columnSettings);
    this.defaultColumns = _.cloneDeep(defaultColumns);
  }

  async getBuyers(accountId, getStats, updateStatsObj) {
    const url = `${this.serviceBaseUri}${accountId}/buyers`;
    const params = {
      ...(getStats ? { includeStats: true } : undefined)
    };
    try {
      const response = await this.$http({
        url: url,
        method: 'GET',
        params: params
      });

      const buyers = updateStatsObj
        ? updateStatsObj.targets
        : response.data.buyers;

      if (getStats) {
        this.statsService.normalizeStats(
          buyers,
          response.data.stats,
          response.data.caps,
          blockReasonTranslation
        );
      }

      response.data.buyers = buyers;
      return response;
    } catch {
      return { data: { buyers: [] } };
    }
  }
  async getCachedBuyers(accountId) {
    const url = `${this.serviceBaseUri}${accountId}/buyers`;

    try {
      return await this.promiseCacheService.getCallWithCachedPromise(
        'buyersGetPromise' + accountId,
        url,
        60
      );
    } catch {
      return {
        data: {
          buyers: []
        }
      };
    }
  }

  getBuyer(accountId, buyerId) {
    const url = this.serviceBaseUri + accountId + '/buyers/' + buyerId;
    return this.$http.get(url);
  }

  getBuyerReferences(accountId, buyerId) {
    const url =
      this.serviceBaseUri +
      accountId +
      '/buyers/' +
      buyerId +
      '/InboundReferences';
    return this.$http.get(url);
  }

  deleteBuyer(accountId, buyerId, force) {
    const url =
      this.serviceBaseUri +
      accountId +
      '/buyers/' +
      buyerId +
      '?force=' +
      !!force;
    return this.$http.delete(url);
  }

  createBuyer(accountId, buyer) {
    const url = this.serviceBaseUri + accountId + '/buyers';
    return this.$http.post(url, buyer);
  }

  updateBuyer(accountId, buyer) {
    const sanitizedBuyer = this.#sanitizeBuyer(buyer);
    const url = this.serviceBaseUri + accountId + '/buyers/' + buyer.id;
    return this.$http.patch(url, sanitizedBuyer);
  }

  linkBuyerToTarget(accountId, targetId, buyer) {
    const url =
      this.serviceBaseUri + accountId + '/targets/' + targetId + '/buyer';
    return this.$http.put(url, buyer);
  }

  unlinkBuyerTarget(accountId, targetId, buyerId) {
    const url =
      this.serviceBaseUri +
      accountId +
      '/targets/' +
      targetId +
      '/buyer/' +
      buyerId;
    return this.$http.delete(url);
  }

  resetCallCounters(accountId, buyerId) {
    const url =
      this.serviceBaseUri + accountId + '/Buyers/' + buyerId + '/ResetCount';
    return this.$http.delete(url);
  }

  linkToItem(accountId, buyerId, item) {
    const url =
      this.serviceBaseUri + accountId + '/buyers/' + buyerId + '/link';
    return this.$http.patch(url, item);
  }

  unlinkItem(accountId, buyerId, item) {
    const url =
      this.serviceBaseUri + accountId + '/buyers/' + buyerId + '/unlink';
    return this.$http.patch(url, item);
  }

  getRestrictionTypes() {
    return this._.cloneDeep(duplicateRestrictionTypes);
  }

  getRestrictionText(restrictionType) {
    if (!restrictionType) {
      return duplicateRestrictionTypes.find(function(type) {
        return type.default;
      }).name;
    }

    return duplicateRestrictionTypes.find(function(type) {
      return type.value === restrictionType.toLowerCase();
    }).name;
  }

  #sanitizeBuyer(buyer) {
    const sanitizedBuyer = this._.cloneDeep(buyer);
    if (sanitizedBuyer && sanitizedBuyer.email === '') {
      delete sanitizedBuyer.email;
    }
    delete sanitizedBuyer.showEditForm;

    sanitizedBuyer.usageStats = undefined;
    sanitizedBuyer.tStatus = undefined;
    sanitizedBuyer.caps = undefined;

    if (sanitizedBuyer.routeDuplicates) {
      sanitizedBuyer.routeDuplicates.trackBy = sanitizedBuyer.routeDuplicates
        .trackBy
        ? sanitizedBuyer.routeDuplicates.trackBy.toLowerCase()
        : null;
      if (
        sanitizedBuyer.routeDuplicates.trackBy !== 'target' &&
        sanitizedBuyer.routeDuplicates.trackBy !== 'buyer'
      ) {
        sanitizedBuyer.routeDuplicates = null;
      }
    }

    return sanitizedBuyer;
  }

  updateBuyerReferencesStateName(buyerReferences) {
    if (!buyerReferences?.hasReferences) return;

    for (let i = 0; i < buyerReferences.targets.length; i++) {
      if (buyerReferences.targets[i].targetId.substring(0, 2) === 'PI') {
        buyerReferences.targets[i].targetEdit = this.#targetEdit(
          'dashboard.ringtreetarget.edit',
          {
            name: buyerReferences.targets[i].targetName,
            pingTreeTargetId: buyerReferences.targets[i].targetId
          }
        );
        buyerReferences.targets[i].targetEditTooltip =
          'DELETE_PING_TREE_MODAL_VIEW_TARGET';
      } else {
        buyerReferences.targets[i].targetEdit = this.#targetEdit(
          'dashboard.target.edit',
          {
            name: buyerReferences.targets[i].targetName,
            targetId: buyerReferences.targets[i].targetId
          }
        );
        buyerReferences.targets[i].targetEditTooltip =
          'DELETE_BUYER_MODAL_VIEW_TARGET';
      }
    }
    return buyerReferences;
  }
  #targetEdit(targetStateName, targetStateParams) {
    return {
      targetStateName: targetStateName,
      targetStateParams: targetStateParams
    };
  }
}
